import React from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";

import { useUserDagsAccountTransactionListViewQuery, TransactionStatus } from "../../../../../graphql/schema";
import { StatusIcon, StatusIconStatus } from "../../../../../components/status-icon/StatusIcon";
import { Section } from "../../../../../components/section/Section";
import { Loader } from "../../../../../components/loader/Loader";
import NotFound from "../../../../../components/not-found/NotFound";
import { StatusLabel, StatusLabelColor } from "../../../../../components/status-label/StatusLabel";
import assertNever from "../../../../../services/assertNever";
import DateTime from "../../../../../components/date-time/DateTime";
import { formatPrice } from "../../../../../components/price/Price";

import styles from "./transaction-list.module.scss";

const ITEMS_PER_PAGE = 200;

export const UserDagTransactionList: React.FC = () => {
  // accestt translation keys
  const [t] = useTranslation();

  const { data, fetchMore, error, loading, previousData } = useUserDagsAccountTransactionListViewQuery({
    variables: { offset: 0, limit: ITEMS_PER_PAGE },
  });

  const invoiceList = data?.getUserDagAccountLogs.transactions || [];
  const hasInvoices = loading || invoiceList.length > 0;
  const totalRows = data?.getUserDagAccountLogs.totalRows || 0;

  if (error) {
    return <NotFound>{t("Something went wrong. Please reload the page or contact support")}</NotFound>;
  }

  if (!hasInvoices) {
    return <NotFound>{t("You have no transactions")}</NotFound>;
  }

  if (data?.getUserDagAccountLogs.transactions.length === 0) {
    return <></>;
  }
  return (
    <Section gutter={10} withSpace className={styles.wrap}>
      <h1>Transactions</h1>
      <Loader cover visibleOnFirstLoad={{ loading, previousData, data }} />
      <div className={styles["term-grid-header"]}>
        <div /> {/* icon column */}
        <div className={styles.header}>Amount</div>
        <div className={styles.header}>Description</div>
        <div className={styles.header}>Date</div>
        <div className={styles.header}>Status</div>
      </div>
      <InfiniteScroll
        dataLength={invoiceList.length}
        next={() => {
          fetchMore({ variables: { offset: invoiceList.length } });
        }}
        hasMore={!!data && data.getUserDagAccountLogs.transactions.length < totalRows}
        loader={<div>Loading...</div>}
      >
        {data?.getUserDagAccountLogs.transactions.map((row) => (
          <div key={row.id} className={styles["term-grid"]}>
            <StatusIcon
              className={styles.icon}
              status={getStatusIconStatus(row?.transactionStatus ?? TransactionStatus.PENDING)}
            />

            <div className={styles.amount}>{row.amount ? `${formatPrice(row.amount, "DAG")}` : "-"}</div>
            <div>
              <h2 className={styles.title}>
                {row.description || (row.transactionType === "DEPOSIT" ? "Deposit" : "-")}
              </h2>
              {row.wallet && <p className={styles.wallet}>{row.wallet}</p>}
            </div>

            <div>
              <DateTime value={row?.transactionDate ?? ""} />
            </div>

            <div>
              <StatusLabel color={getStatusLabelColor(row?.transactionStatus ?? TransactionStatus.PENDING)}>
                {getStatusIconTranslation(t, row?.transactionStatus ?? TransactionStatus.PENDING)}
              </StatusLabel>
            </div>
          </div>
        ))}
      </InfiniteScroll>
    </Section>
  );
};

function getStatusIconStatus(status: TransactionStatus): StatusIconStatus {
  switch (status) {
    case TransactionStatus.SUCCESS:
      return StatusIconStatus.SUCCESS;

    case TransactionStatus.REJECTED:
      return StatusIconStatus.ERROR;

    case TransactionStatus.PENDING:
      return StatusIconStatus.CANCEL;

    default:
      return assertNever(status);
  }
}

function getStatusLabelColor(status: TransactionStatus): StatusLabelColor {
  switch (status) {
    case TransactionStatus.SUCCESS:
      return StatusLabelColor.GREEN;

    case TransactionStatus.REJECTED:
      return StatusLabelColor.GRAY;

    case TransactionStatus.PENDING:
      return StatusLabelColor.GRAY;

    default:
      return assertNever(status);
  }
}

function getStatusIconTranslation(t: (key: string) => string, status: TransactionStatus) {
  switch (status) {
    case TransactionStatus.REJECTED:
      return t("Rejected");

    case TransactionStatus.PENDING:
      return t("Pending");

    case TransactionStatus.SUCCESS:
      return t("Success");
    default:
      return assertNever(status);
  }
}
